import { useTranslation } from 'react-i18next';
import type { Item } from '../../types/bookkeeping.js';

export interface AdjustmentLineFormValue {
  item_id: string;
  qty_delta: string;
  unit_cost: string;
  description: string;
}

export interface AdjustmentLineRowProps {
  index: number;
  value: AdjustmentLineFormValue;
  items: Item[];
  canRemove: boolean;
  onChange: (v: AdjustmentLineFormValue) => void;
  onRemove: () => void;
}

export function AdjustmentLineRow({
  value,
  items,
  canRemove,
  onChange,
  onRemove,
}: AdjustmentLineRowProps) {
  const { t } = useTranslation('bookkeeping');
  const inventoryItems = items.filter((i) => i.type === 'inventory' && i.is_active);

  function handleItemChange(itemId: string) {
    const item = inventoryItems.find((i) => i.id === itemId);
    if (item) {
      onChange({
        ...value,
        item_id: itemId,
        unit_cost: item.default_cost ?? value.unit_cost,
      });
    } else {
      onChange({ ...value, item_id: itemId });
    }
  }

  const isNegative = value.qty_delta.startsWith('-');

  return (
    <tr className="border-b">
      <td className="px-2 py-1">
        <select
          value={value.item_id}
          onChange={(e) => handleItemChange(e.target.value)}
          className="w-full min-w-32 rounded border px-1 py-0.5 text-sm"
        >
          <option value="">—</option>
          {inventoryItems.map((it) => (
            <option key={it.id} value={it.id}>
              {it.name}
            </option>
          ))}
        </select>
      </td>
      <td className="px-2 py-1">
        <input
          value={value.qty_delta}
          onChange={(e) => onChange({ ...value, qty_delta: e.target.value })}
          placeholder="-1000 / 1000"
          className="w-24 rounded border px-1 py-0.5 text-sm"
        />
      </td>
      <td className="px-2 py-1">
        <input
          value={value.unit_cost}
          onChange={(e) => onChange({ ...value, unit_cost: e.target.value })}
          placeholder="0"
          className="w-24 rounded border px-1 py-0.5 text-sm disabled:bg-slate-100"
          disabled={isNegative}
          title={isNegative ? t('inventory.adjustments.cost_computed_for_negative') : ''}
        />
      </td>
      <td className="px-2 py-1">
        <input
          value={value.description}
          onChange={(e) => onChange({ ...value, description: e.target.value })}
          placeholder={t('inventory.adjustments.description')}
          className="w-40 rounded border px-1 py-0.5 text-sm"
        />
      </td>
      <td className="px-2 py-1">
        <button
          type="button"
          onClick={onRemove}
          disabled={!canRemove}
          className="text-red-600 disabled:opacity-30"
        >
          ×
        </button>
      </td>
    </tr>
  );
}
